import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { tasksAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface Task {
  _id: string;
  title: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  dueDate: string;
  isCompleted: boolean;
  isOverdue: boolean;
  client?: {
    _id: string;
    firstName: string;
    lastName: string;
    trackerStatus: string;
    careLevel?: string;
    budget?: number;
    preferences?: {
      location?: string;
      amenities?: string[];
      specialties?: string[];
    };
  };
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    role?: string;
  };
  matchedFacilities?: Array<{
    facilityId: string;
    facilityName: string;
    compatibilityScore: number;
    reasons: string[];
    available: boolean;
  }>;
  progress: {
    percentage: number;
    milestones: Array<{
      name: string;
      completed: boolean;
      completedAt?: string;
    }>;
  };
  communications: Array<{
    id: string;
    type: 'note' | 'call' | 'email' | 'meeting';
    content: string;
    author: string;
    timestamp: string;
  }>;
  estimatedPlacementDate?: string;
  lastActivity: string;
  stage?: string;
}

interface TaskDetailModalProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  task,
  isOpen,
  onClose,
  onUpdateTask
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'communications' | 'matching'>('overview');
  const [newCommunication, setNewCommunication] = useState({
    type: 'note' as 'note' | 'call' | 'email' | 'meeting',
    content: ''
  });

  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  // Add communication mutation
  const addCommunicationMutation = useMutation({
    mutationFn: (communication: { type: string; content: string }) =>
      tasksAPI.addCommunication(task!._id, communication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swimLanes'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      showSuccess('Communication added successfully!');
      setNewCommunication({ type: 'note', content: '' });
    },
    onError: () => {
      showError('Failed to add communication');
    }
  });

  if (!isOpen || !task) return null;

  const handleAddCommunication = () => {
    if (!newCommunication.content.trim()) return;
    addCommunicationMutation.mutate(newCommunication);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getCommunicationIcon = (type: string) => {
    switch (type) {
      case 'call': return <PhoneIcon className="w-4 h-4" />;
      case 'email': return <EnvelopeIcon className="w-4 h-4" />;
      case 'meeting': return <BuildingOfficeIcon className="w-4 h-4" />;
      default: return <ChatBubbleLeftRightIcon className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{task.title}</h2>
                <p className="text-sm text-gray-600">
                  {task.client?.firstName || 'No client'} {task.client?.lastName || ''}
                </p>
              </div>
              <span className={`
                px-3 py-1 text-sm font-medium rounded-full border
                ${getPriorityColor(task.priority)}
              `}>
                {task.priority.toUpperCase()}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('communications')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'communications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Communications
            </button>
            <button
              onClick={() => setActiveTab('matching')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'matching'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Facility Matching
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Progress Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Progress</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                      <span className="text-sm font-medium text-gray-900">
                        {task.progress?.percentage || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                        style={{ width: `${task.progress?.percentage || 0}%` }}
                      ></div>
                    </div>
                    
                    {/* Milestones */}
                    {task.progress?.milestones && task.progress.milestones.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <span className="text-sm font-medium text-gray-700">Milestones</span>
                        {task.progress.milestones.map((milestone, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              milestone.completed 
                                ? 'bg-green-500 border-green-500' 
                                : 'border-gray-300'
                            }`}>
                              {milestone.completed && (
                                <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                              )}
                            </div>
                            <span className={`text-sm ${
                              milestone.completed ? 'text-gray-900' : 'text-gray-500'
                            }`}>
                              {milestone.name}
                            </span>
                            {milestone.completedAt && (
                              <span className="text-xs text-gray-400">
                                {formatDate(milestone.completedAt)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Details */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Client Details</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-5 h-5 text-gray-400" />
                      <span className="font-medium">{task.client?.firstName || 'No client'} {task.client?.lastName || ''}</span>
                    </div>
                    {task.client?.careLevel && (
                      <div>
                        <span className="text-sm text-gray-600">Care Level: </span>
                        <span className="text-sm font-medium">{task.client?.careLevel}</span>
                      </div>
                    )}
                    {task.client?.budget && (
                      <div>
                        <span className="text-sm text-gray-600">Budget: </span>
                        <span className="text-sm font-medium">{task.client?.budget?.toLocaleString()}</span>
                      </div>
                    )}
                    {task.client?.preferences?.location && (
                      <div>
                        <span className="text-sm text-gray-600">Preferred Location: </span>
                        <span className="text-sm font-medium">{task.client?.preferences?.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Assignment Details */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Assignment</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">Assigned To: </span>
                      <span className="text-sm font-medium">
                        {task.assignedTo?.firstName || 'Unassigned'} {task.assignedTo?.lastName || ''}
                      </span>
                      {task.assignedTo?.role && (
                        <span className="text-sm text-gray-500 ml-2">({task.assignedTo?.role})</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Due Date: </span>
                      <span className="text-sm font-medium">{formatDate(task.dueDate)}</span>
                    </div>
                    {task.estimatedPlacementDate && (
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="w-5 h-5 text-green-400" />
                        <span className="text-sm text-gray-600">Est. Placement: </span>
                        <span className="text-sm font-medium text-green-600">
                          {formatDate(task.estimatedPlacementDate)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'communications' && (
              <div className="space-y-6">
                {/* Add New Communication */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Add Communication</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={newCommunication.type}
                        onChange={(e) => setNewCommunication(prev => ({ 
                          ...prev, 
                          type: e.target.value as 'note' | 'call' | 'email' | 'meeting' 
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="note">Note</option>
                        <option value="call">Phone Call</option>
                        <option value="email">Email</option>
                        <option value="meeting">Meeting</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                      <textarea
                        value={newCommunication.content}
                        onChange={(e) => setNewCommunication(prev => ({ 
                          ...prev, 
                          content: e.target.value 
                        }))}
                        placeholder="Enter communication details..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <button
                      onClick={handleAddCommunication}
                      disabled={!newCommunication.content.trim()}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>Add Communication</span>
                    </button>
                  </div>
                </div>

                {/* Communication History */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Communication History</h3>
                  <div className="space-y-3">
                    {task.communications && task.communications.length > 0 ? (
                      task.communications
                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                        .map((comm) => (
                          <div key={comm.id} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                                {getCommunicationIcon(comm.type)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {comm.type.charAt(0).toUpperCase() + comm.type.slice(1)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(comm.timestamp)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700">{comm.content}</p>
                                <p className="text-xs text-gray-500 mt-1">by {comm.author}</p>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No communications recorded yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'matching' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Facility Matches</h3>
                {task.matchedFacilities && task.matchedFacilities.length > 0 ? (
                  <div className="space-y-4">
                    {task.matchedFacilities
                      .sort((a, b) => b.compatibilityScore - a.compatibilityScore)
                      .map((facility, index) => (
                        <div key={facility.facilityId} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-gray-900">{facility.facilityName}</h4>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-2xl font-bold text-blue-600">
                                  {Math.round(facility.compatibilityScore)}%
                                </span>
                                <span className="text-sm text-gray-500">compatibility</span>
                                {facility.available && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Available
                                  </span>
                                )}
                              </div>
                            </div>
                            {index === 0 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Best Match
                              </span>
                            )}
                          </div>
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium text-gray-700">Match Reasons:</h5>
                            <ul className="space-y-1">
                              {facility.reasons.map((reason, reasonIndex) => (
                                <li key={reasonIndex} className="text-sm text-gray-600 flex items-center space-x-2">
                                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                                  <span>{reason}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No facility matches available</p>
                    <p className="text-sm">Smart matching will appear here once client preferences are set</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
